import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SortableHeader, sortData } from "../components/SortableHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Users, CreditCard, TrendingUp, Euro, Search, Edit2, Trash2, RefreshCw,
  Crown, Coffee, Wine, ChefHat, Check, X, Calendar, Clock, AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [transactionStats, setTransactionStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPlan, setNewPlan] = useState("");
  const [sortConfigSubs, setSortConfigSubs] = useState({ key: null, direction: 'asc' });
  const [sortConfigTrans, setSortConfigTrans] = useState({ key: 'created_at', direction: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, transRes] = await Promise.all([
        axios.get(`${API}/api/admin/subscriptions/all`, { withCredentials: true }),
        axios.get(`${API}/api/admin/subscriptions/transactions`, { withCredentials: true })
      ]);
      setSubscriptions(subsRes.data.subscriptions || []);
      setStats(subsRes.data.stats || {});
      setTransactions(transRes.data.transactions || []);
      setTransactionStats(transRes.data.stats || {});
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPlanIcon = (plan) => {
    switch (plan) {
      case 'getraenke': return <Wine size={16} className="text-blue-400" />;
      case 'cafe': return <Coffee size={16} className="text-amber-400" />;
      case 'restaurant': return <ChefHat size={16} className="text-violet-400" />;
      default: return <Crown size={16} className="text-slate-400" />;
    }
  };

  const getPlanBadge = (plan) => {
    const badges = {
      'free': 'bg-slate-500/20 text-slate-400',
      'getraenke': 'bg-blue-500/20 text-blue-400',
      'cafe': 'bg-amber-500/20 text-amber-400',
      'restaurant': 'bg-violet-500/20 text-violet-400'
    };
    return badges[plan] || badges.free;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': 'bg-emerald-500/20 text-emerald-400',
      'trialing': 'bg-blue-500/20 text-blue-400',
      'cancelled': 'bg-red-500/20 text-red-400',
      'past_due': 'bg-amber-500/20 text-amber-400',
      'paid': 'bg-emerald-500/20 text-emerald-400',
      'pending': 'bg-amber-500/20 text-amber-400',
      'failed': 'bg-red-500/20 text-red-400'
    };
    return badges[status] || 'bg-slate-500/20 text-slate-400';
  };

  const openEditDialog = (sub) => {
    setSelectedUser(sub);
    setNewPlan(sub.plan);
    setEditDialogOpen(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlan) return;
    try {
      await axios.put(
        `${API}/api/admin/subscriptions/user/${selectedUser.user_id}/plan`,
        { plan: newPlan },
        { withCredentials: true }
      );
      toast.success("Plan erfolgreich aktualisiert");
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleCancelSubscription = async (userId) => {
    if (!confirm("Subscription wirklich kündigen?")) return;
    try {
      await axios.delete(
        `${API}/api/admin/subscriptions/user/${userId}`,
        { withCredentials: true }
      );
      toast.success("Subscription gekündigt");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Kündigen");
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const search = searchTerm.toLowerCase();
    return (
      sub.user?.name?.toLowerCase().includes(search) ||
      sub.user?.username?.toLowerCase().includes(search) ||
      sub.user?.email?.toLowerCase().includes(search) ||
      sub.plan?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Gesamt Abos</p>
              <p className="text-2xl font-mono font-bold">{stats.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Aktive Abos</p>
              <p className="text-2xl font-mono font-bold text-emerald-500">{stats.active || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Transaktionen</p>
              <p className="text-2xl font-mono font-bold">{transactionStats.paid_transactions || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Euro className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Umsatz Gesamt</p>
              <p className="text-2xl font-mono font-bold text-amber-500">
                {(transactionStats.total_revenue || 0).toFixed(2).replace('.', ',')}€
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Distribution */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-slate-400" />
          Verteilung nach Plan
        </h3>
        <div className="flex gap-4 flex-wrap">
          {['getraenke', 'cafe', 'restaurant'].map(plan => (
            <div key={plan} className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700/50 rounded-lg px-4 py-2">
              {getPlanIcon(plan)}
              <span className="text-sm font-medium capitalize">{plan}</span>
              <span className="bg-slate-200 dark:bg-slate-600 px-2 py-0.5 rounded text-sm font-mono">
                {stats.by_plan?.[plan] || 0}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="subscriptions">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1">
          <TabsTrigger value="subscriptions" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 gap-2">
            <Users size={16} /> Abonnements ({subscriptions.length})
          </TabsTrigger>
          <TabsTrigger value="transactions" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 gap-2">
            <CreditCard size={16} /> Transaktionen ({transactions.length})
          </TabsTrigger>
        </TabsList>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          {/* Search */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Name, E-Mail oder Plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={fetchData} className="gap-2">
              <RefreshCw size={16} /> Aktualisieren
            </Button>
          </div>

          {/* Table */}
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableHeader label="Benutzer" sortKey="user.name" sortConfig={sortConfigSubs} onSort={setSortConfigSubs} />
                  <SortableHeader label="Plan" sortKey="plan" sortConfig={sortConfigSubs} onSort={setSortConfigSubs} />
                  <SortableHeader label="Status" sortKey="status" sortConfig={sortConfigSubs} onSort={setSortConfigSubs} />
                  <SortableHeader label="Seit" sortKey="current_period_start" sortConfig={sortConfigSubs} onSort={setSortConfigSubs} />
                  <SortableHeader label="Läuft bis" sortKey="current_period_end" sortConfig={sortConfigSubs} onSort={setSortConfigSubs} />
                  <th className="text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-500">
                      Keine Abonnements gefunden
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((sub) => (
                    <tr key={sub.id || sub.user_id}>
                      <td>
                        <div>
                          <p className="font-medium">{sub.user?.name || 'Unbekannt'}</p>
                          <p className="text-xs text-slate-500">{sub.user?.email || sub.user?.username}</p>
                        </div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getPlanBadge(sub.plan)}`}>
                          {getPlanIcon(sub.plan)}
                          <span className="capitalize">{sub.plan}</span>
                        </span>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(sub.status)}`}>
                          {sub.status === 'active' ? <Check size={12} /> : <Clock size={12} />}
                          {sub.status}
                        </span>
                      </td>
                      <td className="text-sm">{formatDate(sub.current_period_start)}</td>
                      <td className="text-sm">{formatDate(sub.current_period_end)}</td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(sub)}
                            className="h-8 w-8 p-0"
                            title="Plan bearbeiten"
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelSubscription(sub.user_id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Kündigen"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableHeader label="Datum" sortKey="created_at" sortConfig={sortConfigTrans} onSort={setSortConfigTrans} />
                  <SortableHeader label="Benutzer" sortKey="user_id" sortConfig={sortConfigTrans} onSort={setSortConfigTrans} />
                  <SortableHeader label="Plan" sortKey="plan" sortConfig={sortConfigTrans} onSort={setSortConfigTrans} />
                  <SortableHeader label="Betrag" sortKey="amount" sortConfig={sortConfigTrans} onSort={setSortConfigTrans} />
                  <SortableHeader label="Status" sortKey="status" sortConfig={sortConfigTrans} onSort={setSortConfigTrans} />
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-slate-500">
                      Keine Transaktionen vorhanden
                    </td>
                  </tr>
                ) : (
                  sortData(transactions, sortConfigTrans).map((trans) => (
                    <tr key={trans.id}>
                      <td className="text-sm">{formatDateTime(trans.created_at)}</td>
                      <td>
                        <p className="text-sm">{trans.metadata?.username || trans.user_id?.substring(0, 8)}</p>
                        <p className="text-xs text-slate-500">{trans.metadata?.user_email}</p>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getPlanBadge(trans.plan)}`}>
                          {getPlanIcon(trans.plan)}
                          <span className="capitalize">{trans.plan}</span>
                        </span>
                      </td>
                      <td className="font-mono font-medium">
                        {trans.amount?.toFixed(2).replace('.', ',')}€
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(trans.status)}`}>
                          {trans.status === 'paid' ? <Check size={12} /> : 
                           trans.status === 'failed' ? <X size={12} /> : 
                           <Clock size={12} />}
                          {trans.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Plan Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Plan ändern</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-sm text-slate-500 mb-2">Benutzer: <strong>{selectedUser?.user?.name}</strong></p>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Plan auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free (Demo)</SelectItem>
                  <SelectItem value="getraenke">Getränke (9,99€)</SelectItem>
                  <SelectItem value="cafe">Café (34,99€)</SelectItem>
                  <SelectItem value="restaurant">Restaurant (59,99€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button className="flex-1" onClick={handleUpdatePlan}>
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptions;
